import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from "react";
import CustomDataGrid from "./CustomDataGrid";
import axios from "axios";
import useFetch from "../../../hook/useFetch";
import useDebounce from "../../../hook/useDebounce";
import { use } from "react";

/**
 * ServerPaginationDataGrid component for handling server-side pagination with React Query.
 *
 * @param {string} url - The URL to fetch data from.
 * @param {string} search - text to fetch data from search parameter.
 * @param {object} requestParameter - Additional parameters for the request.
 * @param {number} limit - The number of items per page.
 * @param {array} columns - The columns to display in the data grid.
 * @param {boolean} isServerPagination - Flag to enable server-side pagination.
 * @param {string} keyId - The unique identifier key for rows.
 * @param {function} dataMapper - Function to map server response data, should return an object with 'list' as Array and 'total' as number. eg. return { list: [], total: number }
 * @return {JSX.Element} CustomDataGrid component with pagination and data display.
 */
const ServerPaginationDataGridWithQuery = forwardRef(
    (
        {
            url = "https://dummyjson.com/products",
            requestParameter = {},
            limit = 10,
            columns = [],
            isServerPagination = false,
            keyId,
            dataMapper = rows => rows,
            search = "",
        },
        ref,
    ) => {
        const [Reflector, setReflector] = useState(Date.now());
        const [searchValue, setSearchValue] = useState(search);
        // const [pageNumber, setPageNumber] = useState(1);
        const [requestParamsLocal, setRequestParamsLocal] = useState({});

        const pageNumber = useRef(1);
        const list = useRef([]);
        const metadata = useRef(null);

        useDebounce(
            () => {
                setSearchValue(search);
            },
            [search],
            { delay: 500, skipFirstCall: true },
        );

        useEffect(() => {
            pageNumber.current = 1;
            setReflector(Date.now());
            setRequestParamsLocal({
                ...requestParameter,
                limit: limit
            });
        }, [requestParameter])
        

        // const params = JSON.stringify(requestParameter);
        // params.limit = limit;
        // params.search = searchValue;
        // params.page_number = pageNumber.current;

        const { data, isLoading, isError, refetch } = useFetch({
            url: url,
            search: searchValue,
            params: {
                ...requestParamsLocal,
                search: searchValue,
                page_number: pageNumber.current
            },
            // params: {
            //     ...JSON.parse(params),
            //     limit,
            //     search: searchValue,
            //     page_number: pageNumber.current,
            // },
            onSuccess: (data) => {
                // console.log("OnSuccess", data);
                
                if (data?.data?.metadata?.page == 1) {
                    list.current = dataMapper(data?.data?.data);
                } else {
                    list.current = list.current.concat(dataMapper(data?.data?.data));
                }
                metadata.current = data?.data?.metadata;
                if (!metadata.current?.total_count) {
                    if (data?.data?.data?.length < limit) {
                        metadata.current.total_count = list.current?.length;
                    } else {
                        metadata.current.total_count = list.current?.length+1;
                    }
                }

                setReflector(Date.now());
            }
        });

        // console.log({ data: data?.data });
        useImperativeHandle(ref, () => ({
            restartComponent: () => {
                pageNumber.current = 1;
                setReflector(Date.now());
                refetch();
            },
        }));

        return (
            <CustomDataGrid
                keyId={keyId}
                onPageChange={_pageNumber => {
                    if (isServerPagination) {
                        pageNumber.current = _pageNumber;
                        setReflector(Date.now());
                        // setTimeout(() => {
                        //     refetch();
                        // }, 500);
                    }
                }}
                isLoading={isLoading}
                columns={columns}
                rows={list?.current}
                pageNumber={isServerPagination ? pageNumber.current : null}
                perPageSize={limit}
                totalPages={Math.ceil(metadata.current?.total_count / limit) || 0}
                isError={isError}
            />
        );
    },
);

export default React.memo(ServerPaginationDataGridWithQuery);
